import { useState, useEffect, useCallback } from "react";
import { ITEMS_PER_PAGE } from "../../../constants";
import {
  showErrorToast,
  getDuplicateSubmissionsReportsByType,
} from "../../../helpers";
import { getSelectedJournalIds } from "../../../helpers/journals";

const useDuplicateSubmissions = ({ type, filter }) => {
  const [data, setData] = useState([]);
  const [error, setError] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [fetchParams, setFetchParams] = useState({
    currentPage: 0,
    searchTerm: "",
    type,
    filter,
  });

  useEffect(() => {
    setFetchParams((prev) => {
      if (prev.filter === filter) return prev;
      return { ...prev, filter, currentPage: 0 };
    });
  }, [filter]);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(false);

      const selectedJournalIds = await getSelectedJournalIds();
      const { currentPage, type, filter, searchTerm } = fetchParams;
      const appliedFilter = type === "archived" ? searchTerm : filter;

      const { data: { reports = [], total = 0 } = {} } =
        await getDuplicateSubmissionsReportsByType(
          currentPage * ITEMS_PER_PAGE + 1,
          ITEMS_PER_PAGE,
          type,
          appliedFilter,
          selectedJournalIds
        );

      setData(reports);
      setPageCount(Math.ceil(total / ITEMS_PER_PAGE));
    } catch {
      showErrorToast();
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [fetchParams]);

  const fetchAllDataForExport = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(false);

      const selectedJournalIds = await getSelectedJournalIds();
      const { type, filter, searchTerm } = fetchParams;
      const appliedFilter = type === "archived" ? searchTerm : filter;

      const { data: { reports = [] } = {} } =
        await getDuplicateSubmissionsReportsByType(
          1,
          10000,
          type,
          appliedFilter,
          selectedJournalIds
        );

      return reports;
    } catch {
      showErrorToast();
      setError(true);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [fetchParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateFetchParams = (newParams) => {
    setFetchParams((prev) => ({
      ...prev,
      ...newParams,
    }));
  };

  return {
    data,
    setData,
    error,
    pageCount,
    currentPage: fetchParams.currentPage,
    searchTerm: fetchParams.searchTerm,
    setSearchTerm: (term) =>
      updateFetchParams({ searchTerm: term, currentPage: 0 }),
    setCurrentPage: (page) => updateFetchParams({ currentPage: page }),
    fetchData,
    fetchAllDataForExport,
    isLoading,
  };
};

export default useDuplicateSubmissions;